import { isArray, isEmpty, isNil, snakeCase } from 'lodash';

import { colors } from './utils';

import { __t, applyTranslations } from 'utils/language';
import { abbrNum } from 'utils/helper/common';
import { renderPrice } from 'utils/helper/price';

import type {
  ChartFormatterParams,
  ChartLabel,
  ChartSeriesData,
  ChartTooltip,
  ChartTooltipType,
  ChartType,
  PieChart,
  PieChartData,
  PieChartPreparationOptions,
  StatisticsCommon,
  StatisticsTiers
} from 'types';

applyTranslations(require('../i18n/pie-chart-utils.i18n.json'));

export const getBudgetPieChartData = (data?: Array<StatisticsCommon> | null): Array<PieChart> => (data || []).map(({ label, absolute, emphasis }) => ({
  label: __t(`pie_chart_util.budget.${(label || '').toLowerCase()}`),
  metric: absolute || 0,
  showPrice: true,
  emphasis
}));

export const getImpressionsPieChartData = (data?: Array<StatisticsCommon> | null): Array<PieChart> => (data || []).map(({ label, absolute, emphasis }) => ({
  label: __t(`pie_chart_util.impressions.${(label || '').toLowerCase()}`),
  metric: absolute || 0,
  emphasis
}));

export const getReachPieChartData = (data?: Array<StatisticsCommon> | null): Array<PieChart> => (data || []).map(({ label, absolute, emphasis }) => ({
  label: __t(`pie_chart_util.reach.${(label || '').toLowerCase()}`),
  metric: absolute || 0,
  emphasis
}));

export const getGenderPieChartData = (data?: Array<StatisticsCommon> | null): Array<PieChart> => (data || []).map(({ label, absolute }) => ({
  label: __t(`pie_chart_util.genders.${(label || '').toLowerCase()}`),
  description: __t('pie_chart_util.genders.gender'),
  metric: absolute || 0
}));

export const getEngagementPieChartData = (data?: Array<StatisticsCommon> | null): Array<PieChart> => (data || []).map(({ label, absolute, emphasis }) => ({
  label: __t(`pie_chart_util.engagement.${snakeCase(label || '')}`),
  metric: absolute || 0,
  emphasis
}));

export const getPerceptionPieChartData = (data?: Array<StatisticsCommon> | null): Array<PieChart> => (data || []).map(({ label, absolute, emphasis }) => ({
  label: __t(`pie_chart_util.perception.${snakeCase(label || '')}`),
  metric: absolute || 0,
  emphasis
}));

export const getAgePieChartData = (data?: Array<StatisticsCommon> | null): Array<PieChart> => (data || []).map(({ label, absolute }) => ({
  label,
  description: __t('pie_chart_util.age'),
  metric: absolute || 0
}));

export const getFollowersPieChartData = (data?: Array<StatisticsTiers> | null): Array<PieChart> => (data || []).map(({ label, absolute }) => ({
  label: __t(`pie_chart_util.influencer_tiers_short.${label}`),
  description: __t(`pie_chart_util.influencer_tiers_abbreviated.${label}`),
  metric: absolute || 0
}));

export const getOverlapPieChartData = (data?: Array<StatisticsCommon> | null): Array<PieChart> => (data || []).map(({ label, absolute }) => ({
  label: __t(`pie_chart_util.overlap.${label}`),
  metric: absolute || 0
}));

export const displayPieChart = (data: Array<PieChart>): boolean => {
  if (!isArray(data) || isEmpty(data)) {
    return false;
  }

  return data.some(item => item.metric > 0);
};

const getTooltip = (
  params: ChartFormatterParams,
  chartType: ChartType,
  tooltipType?: ChartTooltipType
): string => {
  const styles = `
    display: flex;
    flex-flow: row wrap;
    font-family: Hind;
    font-size: 12px;
    line-height: 18px;
  `;

  let name = tooltipType === 'reversed' ? params.name : params.data.description;
  const description = tooltipType === 'reversed' ? params.data.description : params.name;
  const metric = chartType === 'audience' ? abbrNum(Math.round(params.data.metric)) : Math.round(params.data.metric);
  let additionalDescription = '';

  name = name ? `<strong>${name}</strong>:&nbsp;` : '';

  if (params.data.additionalDescription) {
    additionalDescription = `
      <div style="${styles} margin-top: 8px;">
        ${params.data.additionalDescription}
      </div>
    `;
  }

  let total = params.data.showPercentage ? `${metric}%` : metric;

  if (params.data.showPrice) {
    total = renderPrice(metric);
  }

  return `
    <div style="${styles}">
      ${name}
      <span>
        ${description}
      </span>
    </div>
    <div style="${styles}">
      ${__t('pie_chart_util.total')}: ${total}
    </div>
    ${additionalDescription}
  `;
};

const getTooltipData = (chartType: ChartType, tooltipType?: ChartTooltipType): ChartTooltip => ({
  trigger: 'item',
  padding: [10, 20],
  backgroundColor: '#fff',
  textStyle: {
    color: '#3a3a3a',
    lineHeight: 14
  },
  extraCssText: 'box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.14); border: 1px solid #e0e0e0;',
  position: (mousePosition: Array<number>): Array<number> => [mousePosition[0] - 40, mousePosition[1] + 40],
  formatter: (params: ChartFormatterParams): string => getTooltip(params, chartType, tooltipType)
});

const getSeriesLabelPercentage = (percent: number): string | number => {
  const percentageRounded = Math.round(percent);
  let percentage: string | number = 0;

  if (percentageRounded === 0) {
    percentage = percent.toFixed(1);
  }

  if (percent < 0.1) {
    percentage = percent.toFixed(2);
  }

  if (percent < 0.01) {
    percentage = '0.01';
  }

  return percentage || Math.round(percent);
};

const getSeriesLabelFormatted = (
  name: string,
  metric: number | string,
  percent: number,
  showPercentage?: boolean | null
): string => {
  const percentage = getSeriesLabelPercentage(percent);
  const value = showPercentage ? `${percentage}%` : metric;

  return `{name|${name}:} {value|${value}}${!showPercentage ? ` {percent|(${percentage}%)}` : ''}`;
};

const getSeriesLabel = (params: ChartFormatterParams, chartType: ChartType): string => {
  const metric = chartType === 'audience' ? abbrNum(Math.round(params.data.metric)) : Math.round(params.data.metric);

  return getSeriesLabelFormatted(params.name, metric, params.percent, params.data.showPercentage);
};

const getSeriesLabelData = (chartType: ChartType, options?: PieChartPreparationOptions): ChartLabel => {
  const styles = {
    fontFamily: 'Hind',
    color: '#3a3a3a',
    fontSize: options?.styles?.label?.fontSize ? options.styles.label.fontSize : 12,
    align: options?.styles?.label?.align,
    lineHeight: options?.styles?.label?.lineHeight,
    verticalAlign: options?.styles?.label?.verticalAlign
  };

  const metricStyles = {
    ...styles,
    ...(options?.styles?.metric || {})
  };

  return {
    ...options?.styles?.label,
    formatter: (params: ChartFormatterParams): string => (options?.formatter ? options.formatter(params, chartType) : getSeriesLabel(params, chartType)),
    rich: {
      name: {
        ...styles
      },
      value: {
        ...metricStyles,
        fontWeight: 600
      },
      percent: {
        ...styles,
        padding: [0, 0, 0, 1],
        color: '#8d8d8d'
      }
    }
  };
};

const getSeriesData = (
  chartType: ChartType,
  options?: PieChartPreparationOptions,
  isPadding?: boolean
): ChartSeriesData => ({
  type: 'pie',
  radius: options?.radius || '70%',
  center: ['50%', '50%'],
  color: options?.colors || colors[chartType],
  label: getSeriesLabelData(chartType, options),
  minAngle: 8,
  labelLine: {
    length: 7,
    length2: 5,
    lineStyle: {
      color: '#e0e0e0'
    },
    ...(options?.styles?.labelLine || {})
  },
  top: isPadding ? 23 : 0,
  bottom: 0
});

export const preparePieChartData = (
  data: Array<PieChart>,
  chartType: ChartType,
  options?: PieChartPreparationOptions,
  isPadding?: boolean
): PieChartData | {} => {
  if (isNil(data)) {
    return {};
  }

  const dataset = {
    dimensions: ['label', 'metric'],
    source: data.filter(item => Math.round(item.metric) > 0)
  };

  if (isEmpty(dataset.source)) {
    return {};
  }

  const tooltip = getTooltipData(chartType, options?.tooltipType);
  const series = getSeriesData(chartType, options, isPadding);

  return {
    tooltip,
    dataset,
    series
  };
};
